import { markRaw } from 'vue'
import MainProject from '@/model/MainProject'
import type { ResourceCategoryKey } from '@/features/virtual-resource/constants'
import type DataFileMap from '@/model/Resource/DataFileMap'
import { syncVirtualResourcePathMetadata } from '@/features/virtual-resource/rules'
import { refreshBuiltInVirtualResourceUrls } from '@/features/workspace/usecases/builtInResources'

export type VirtualResourceRootsState = Record<ResourceCategoryKey, DataFileMap | null>

export type OpenedProjectMeta = {
  sourceFileName: string | null
  rootLabel: string | null
  markerFileName: string | null
  rootDirectoryHandle: FileSystemDirectoryHandle | null
  dataDirectoryHandle?: FileSystemDirectoryHandle | null
  isNew: boolean
  /** 云端工程打开时绑定的账号 uid；本地磁盘工程可为空。 */
  ownerUid?: number | null
}

/** 创建一份空的虚拟资源树状态。 */
export function createEmptyVirtualResourceRoots(): VirtualResourceRootsState {
  return {
    audio: null,
    graphic: null,
    font: null,
    video: null,
  }
}

/** 归一化虚拟资源树路径元数据，并刷新内置资源 URL。 */
export function normalizeVirtualResourceRoots(roots: VirtualResourceRootsState): VirtualResourceRootsState {
  return refreshBuiltInVirtualResourceUrls({
    audio: roots.audio ? syncVirtualResourcePathMetadata('audio', roots.audio) : null,
    graphic: roots.graphic ? syncVirtualResourcePathMetadata('graphic', roots.graphic) : null,
    font: roots.font ? syncVirtualResourcePathMetadata('font', roots.font) : null,
    video: roots.video ? syncVirtualResourcePathMetadata('video', roots.video) : null,
  })
}

/** 统一拿到当前已打开工程，不允许调用方反复写相同的空值守卫。 */
export function requireOpenedProject(project: MainProject | null): MainProject {
  if (!project) {
    throw new Error('当前没有已打开工程')
  }
  return project
}

/**
 * 将 `MainProject` 中体量较大且 class 结构复杂的字段标记为 `markRaw`，
 * 避免 Vue 对整棵工程数据做深度响应式代理。
 */
export function prepareProjectForStore(mp: MainProject): MainProject {
  mp.dataMap = markRaw(mp.dataMap)
  mp.projectHeader = markRaw(mp.projectHeader)
  mp.screenHash = markRaw(mp.screenHash)
  mp.systemData = markRaw(mp.systemData)
  mp.gameVar = markRaw(mp.gameVar)
  mp.gameVarEx = markRaw(mp.gameVarEx)
  mp.gameStr = markRaw(mp.gameStr)
  mp.gameArray = markRaw(mp.gameArray)
  mp.gameObj = markRaw(mp.gameObj)
  mp.gameUnite = markRaw(mp.gameUnite)
  mp.userRm = markRaw(mp.userRm)
  mp.projectConstant = markRaw(mp.projectConstant)
  if (mp.GameMallNew) {
    mp.GameMallNew = markRaw(mp.GameMallNew) as typeof mp.GameMallNew
  }
  mp.GameMallLimite = markRaw(mp.GameMallLimite)
  mp.Welfare = markRaw(mp.Welfare)
  /** 剧情树体量大，避免 Vue 对整棵 Maps/Events 做深度响应式。 */
  mp.storyTree = markRaw(mp.storyTree)
  return mp
}

/** 位图字体对象体积较大，挂回 store 前统一转为 raw，避免深层代理。 */
export function markProjectBitmapFontRaw(project: MainProject): void {
  const bitmapFont = project.bitmapFont
  if (bitmapFont) {
    project.bitmapFont = markRaw(bitmapFont) as typeof bitmapFont
  }
}

/** 校验来源 guid 与工程主包内 guid 是否一致，避免误打开其他作品。 */
export function assertProjectHeaderIdMatches(project: MainProject, guid: string, source: string): void {
  const cleanGuid = guid.trim()
  const projectGuid = project.projectHeader.ID?.trim() || ''
  if (!cleanGuid) {
    throw new Error(`${source}缺少来源 guid，无法打开工程`)
  }
  if (!projectGuid) {
    throw new Error(`${source}中的工程主包缺少 guid，无法确认作品身份`)
  }
  if (projectGuid !== cleanGuid) {
    throw new Error(`${source}的 guid 与工程主包不一致：来源=${cleanGuid}，工程=${projectGuid}`)
  }
}
